import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCMS } from '../cms';
import { useCaseSlug } from '../use-case-utils';
import { getMetricBySlug } from '../data/metrics';
import { getStudyBySlug, RESEARCH_INDEX_META } from '../data/research';

// DEV-10856: metrics glossary index meta. The per-metric detail pages
// carry their own title/description from the metrics data module; only
// the /metrics index needs a static entry here.
const METRICS_INDEX_TITLE = 'Metrics Glossary | LunarCrush Social & Market Metrics';
const METRICS_INDEX_DESC =
  'Definitions for every LunarCrush metric — Galaxy Score™, AltRank™, social volume, social dominance, sentiment and more. How each is calculated and how to read it.';

/** Map route pathname → CMS page key */
const ROUTE_TO_PAGE: Record<string, string> = {
  '/':                    'homepage',
  '/enterprise':          'enterprise',
  '/products/lunarcrush-claude':             'claudecode',
  '/products/lunarcrush-chatgpt':            'chatgptplugin',
  '/products/lunarcrush-api':                'api',
  '/products/lunarcrush-mcpserver':          'mcp',
  '/products/lunarcrush-cli':                'cli',
  '/products/lunarcrush-discover':           'discover',
  '/products/lunarcrush-custom-collections': 'collections',
  // DEV-12110: without this the prerendered <title>/<meta> are correct (so
  // crawlers are fine) but the SPA overwrites them with the generic homepage
  // values on hydration — the browser tab read "LunarCrush: Real-Time Social &
  // Market Intelligence" on a page titled "LunarCrush Fantasy Football".
  '/products/fantasy-football':              'fantasy',
  '/about':               'about',
  '/crypto-social-listening': 'crypto-social-listening',
  '/support':             'faq',
  '/terms':               'legal',
  '/privacy':             'legal',
  '/referral-terms':      'legal',
  '/use-cases':           'usecases',
  '/pricing':             'pricing',
  '/careers':             'careers',
  '/blog':                'blog',
};

const DEFAULT_TITLE = 'LunarCrush: Real-Time Social Media Sentiment';
const DEFAULT_DESC = 'Build smarter with real-time social media sentiment. Connect LunarCrush data to Claude, your codebase, APIs, and enterprise applications.';
// DEV-10740 R2: resolve against the live host instead of a hardcoded
// `https://lunarcrush.com` — that legacy zone has no `/api/og` endpoint, so
// the share card 404'd. This hook runs in the browser, so the serving origin
// (lunarcrush.pages.dev, new.lunarcrush.com, lunarcrush.com) is known directly.
const BASE_URL = typeof window !== 'undefined' ? window.location.origin : '';

function setMetaTag(property: string, content: string, isName = false) {
  const attr = isName ? 'name' : 'property';
  let el = document.querySelector(`meta[${attr}="${property}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setOgType(type: string) {
  let el = document.querySelector('meta[property="og:type"]');
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', 'og:type');
    document.head.appendChild(el);
  }
  el.setAttribute('content', type);
}

// Strip markdown / HTML to a plain-text excerpt of N chars. Used to
// derive a meta-description from a blog post body or FAQ answer.
function plainExcerpt(body: string | undefined, maxLen: number): string {
  if (!body) return '';
  return body
    .replace(/```[\s\S]*?```/g, ' ') // code blocks
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ') // images
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1') // links → keep label
    .replace(/[#>*_`~]/g, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, maxLen)
    .trim();
}

type LocaleKey = 'en' | 'es' | 'de' | 'fr' | 'zh';
type ItemTemplate = {
  title: (n: string) => string;
  description: (n: string) => string;
  ogTitle: (n: string) => string;
  ogDescription: (n: string) => string;
  ogType: string;
};
type LocaleTemplates = {
  useCase: ItemTemplate;
  blog: Omit<ItemTemplate, 'description' | 'ogDescription'>;
  faq: Omit<ItemTemplate, 'description' | 'ogDescription'>;
};

// DEV-10568: per-locale templates from the New Site URLs+Redirects
// Google Sheet (5 tabs). Blog and FAQ entries source their
// description from the CMS body, so only title / ogTitle / ogType
// come from the doc — description is computed at render time.
const TEMPLATES: Record<LocaleKey, LocaleTemplates> = {
  en: {
    useCase: {
      title: (n) => `Use Case: ${n} | LunarCrush`,
      description: (n) =>
        `Step-by-step workflow for ${n} using LunarCrush social and market data. Real implementation with live signals.`,
      ogTitle: (n) => `${n} on LunarCrush`,
      ogDescription: (n) => `Real workflow for ${n} — using LunarCrush's live social and market data.`,
      ogType: 'article',
    },
    blog: { title: (n) => `${n} | LunarCrush Blog`, ogTitle: (n) => n, ogType: 'article' },
    faq: { title: (n) => `${n} | LunarCrush Help Center`, ogTitle: (n) => n, ogType: 'article' },
  },
  es: {
    useCase: {
      title: (n) => `Caso de Uso: ${n} | LunarCrush`,
      description: (n) =>
        `Flujo paso a paso para ${n} usando datos sociales y de mercado de LunarCrush. Implementación real con señales en vivo.`,
      ogTitle: (n) => `${n} en LunarCrush`,
      ogDescription: (n) => `Flujo real para ${n} — usando los datos sociales y de mercado en vivo de LunarCrush.`,
      ogType: 'article',
    },
    blog: { title: (n) => `${n} | Blog LunarCrush`, ogTitle: (n) => n, ogType: 'article' },
    faq: { title: (n) => `${n} | Centro de Ayuda LunarCrush`, ogTitle: (n) => n, ogType: 'article' },
  },
  de: {
    useCase: {
      title: (n) => `Use Case: ${n} | LunarCrush`,
      description: (n) =>
        `Schritt-für-Schritt-Workflow für ${n} mit Social- und Marktdaten von LunarCrush. Echte Umsetzung mit Live-Signalen.`,
      ogTitle: (n) => `${n} auf LunarCrush`,
      ogDescription: (n) => `Realer Workflow für ${n} — mit Live-Social- und Marktdaten von LunarCrush.`,
      ogType: 'article',
    },
    blog: { title: (n) => `${n} | LunarCrush Blog`, ogTitle: (n) => n, ogType: 'article' },
    faq: { title: (n) => `${n} | LunarCrush Help Center`, ogTitle: (n) => n, ogType: 'article' },
  },
  fr: {
    useCase: {
      title: (n) => `Cas d'Usage : ${n} | LunarCrush`,
      description: (n) =>
        `Workflow pas-à-pas pour ${n} avec les données sociales et de marché LunarCrush. Implémentation réelle avec signaux en direct.`,
      ogTitle: (n) => `${n} sur LunarCrush`,
      ogDescription: (n) => `Workflow réel pour ${n} — avec les données sociales et de marché en direct de LunarCrush.`,
      ogType: 'article',
    },
    blog: { title: (n) => `${n} | Blog LunarCrush`, ogTitle: (n) => n, ogType: 'article' },
    faq: { title: (n) => `${n} | Centre d'Aide LunarCrush`, ogTitle: (n) => n, ogType: 'article' },
  },
  zh: {
    useCase: {
      title: (n) => `使用案例:${n} | LunarCrush`,
      description: (n) => `使用 LunarCrush 社交与市场数据实现 ${n} 的分步工作流。包含实时信号的真实实现。`,
      ogTitle: (n) => `LunarCrush 上的 ${n}`,
      ogDescription: (n) => `${n} 的真实工作流 — 使用 LunarCrush 的实时社交与市场数据。`,
      ogType: 'article',
    },
    blog: { title: (n) => `${n} | LunarCrush 博客`, ogTitle: (n) => n, ogType: 'article' },
    faq: { title: (n) => `${n} | LunarCrush 帮助中心`, ogTitle: (n) => n, ogType: 'article' },
  },
};

// Per-route overrides for cases where two routes share a single CMS
// page key (DEV-10568). The sheet ships separate metadata for /terms
// vs /privacy even though both render from `legal/page.md`, and ships
// translated copy for all 5 locales.
type RouteOverride = { title: string; description: string; ogTitle: string; ogDescription: string; ogType: string };
const ROUTE_OVERRIDES: Record<string, Record<LocaleKey, RouteOverride>> = {
  '/terms': {
    en: {
      title: 'Terms of Service | LunarCrush',
      description: 'Read the LunarCrush Terms of Service governing use of our website, app, API, and data products.',
      ogTitle: 'LunarCrush Terms of Service',
      ogDescription: 'Terms governing use of the LunarCrush website, app, API, and data products.',
      ogType: 'website',
    },
    es: {
      title: 'Términos del Servicio | LunarCrush',
      description: 'Lee los Términos del Servicio de LunarCrush que regulan el uso del sitio web, app, API y productos de datos.',
      ogTitle: 'Términos del Servicio LunarCrush',
      ogDescription: 'Términos que regulan el uso del sitio web, app, API y productos de datos de LunarCrush.',
      ogType: 'website',
    },
    fr: {
      title: "Conditions d'Utilisation | LunarCrush",
      description: "Lisez les Conditions d'Utilisation LunarCrush régissant l'usage de notre site, app, API et produits de données.",
      ogTitle: "Conditions d'Utilisation LunarCrush",
      ogDescription: "Conditions régissant l'usage du site, de l'app, de l'API et des produits de données LunarCrush.",
      ogType: 'website',
    },
    de: {
      title: 'Nutzungsbedingungen | LunarCrush',
      description: 'Lies die LunarCrush Nutzungsbedingungen für die Nutzung unserer Website, App, API und Datenprodukte.',
      ogTitle: 'LunarCrush Nutzungsbedingungen',
      ogDescription: 'Bedingungen für die Nutzung von Website, App, API und Datenprodukten von LunarCrush.',
      ogType: 'website',
    },
    zh: {
      title: '服务条款 | LunarCrush',
      description: '阅读 LunarCrush 服务条款,了解网站、应用、API 和数据产品的使用规定。',
      ogTitle: 'LunarCrush 服务条款',
      ogDescription: '管辖 LunarCrush 网站、应用、API 和数据产品使用的条款。',
      ogType: 'website',
    },
  },
  '/privacy': {
    en: {
      title: 'Privacy Policy | LunarCrush',
      description: 'How LunarCrush collects, uses, and protects personal data across our website, app, and API services.',
      ogTitle: 'LunarCrush Privacy Policy',
      ogDescription: 'How LunarCrush collects, uses, and protects personal data across our website, app, and API.',
      ogType: 'website',
    },
    es: {
      title: 'Política de Privacidad | LunarCrush',
      description: 'Cómo LunarCrush recopila, usa y protege datos personales en el sitio web, app y servicios API.',
      ogTitle: 'Política de Privacidad LunarCrush',
      ogDescription: 'Cómo LunarCrush recopila, usa y protege datos personales en sitio web, app y API.',
      ogType: 'website',
    },
    fr: {
      title: 'Politique de Confidentialité | LunarCrush',
      description: 'Comment LunarCrush collecte, utilise et protège les données personnelles sur notre site, app et services API.',
      ogTitle: 'Politique de Confidentialité LunarCrush',
      ogDescription: 'Comment LunarCrush collecte, utilise et protège les données personnelles sur site, app et API.',
      ogType: 'website',
    },
    de: {
      title: 'Datenschutzerklärung | LunarCrush',
      description: 'Wie LunarCrush personenbezogene Daten auf Website, App und API erhebt, nutzt und schützt.',
      ogTitle: 'LunarCrush Datenschutzerklärung',
      ogDescription: 'Wie LunarCrush personenbezogene Daten auf Website, App und API erhebt, nutzt und schützt.',
      ogType: 'website',
    },
    zh: {
      title: '隐私政策 | LunarCrush',
      description: 'LunarCrush 如何在网站、应用和 API 服务中收集、使用和保护个人数据。',
      ogTitle: 'LunarCrush 隐私政策',
      ogDescription: 'LunarCrush 如何在网站、应用和 API 中收集、使用和保护个人数据。',
      ogType: 'website',
    },
  },
  // DEV-11337: /referral-terms shares the `legal` CMS page key with /terms
  // and /privacy, so it needs its own per-route metadata here too.
  '/referral-terms': {
    en: {
      title: 'Referral Program Terms | LunarCrush',
      description: 'The terms and conditions governing the LunarCrush Referral Program, including rewards, payments, and participant responsibilities.',
      ogTitle: 'LunarCrush Referral Program Terms',
      ogDescription: 'Terms governing the LunarCrush Referral Program: rewards, payments, and participant responsibilities.',
      ogType: 'website',
    },
    es: {
      title: 'Términos del Programa de Referidos | LunarCrush',
      description: 'Los términos y condiciones que rigen el Programa de Referidos de LunarCrush, incluidas recompensas, pagos y responsabilidades.',
      ogTitle: 'Términos del Programa de Referidos de LunarCrush',
      ogDescription: 'Términos que rigen el Programa de Referidos de LunarCrush: recompensas, pagos y responsabilidades.',
      ogType: 'website',
    },
    fr: {
      title: 'Conditions du Programme de Parrainage | LunarCrush',
      description: 'Les conditions régissant le Programme de Parrainage LunarCrush : récompenses, paiements et responsabilités des participants.',
      ogTitle: 'Conditions du Programme de Parrainage LunarCrush',
      ogDescription: 'Conditions régissant le Programme de Parrainage LunarCrush : récompenses, paiements et responsabilités.',
      ogType: 'website',
    },
    de: {
      title: 'Bedingungen des Empfehlungsprogramms | LunarCrush',
      description: 'Die Bedingungen für das LunarCrush Empfehlungsprogramm: Prämien, Auszahlungen und Pflichten der Teilnehmer.',
      ogTitle: 'LunarCrush Empfehlungsprogramm-Bedingungen',
      ogDescription: 'Bedingungen für das LunarCrush Empfehlungsprogramm: Prämien, Auszahlungen und Pflichten der Teilnehmer.',
      ogType: 'website',
    },
    zh: {
      title: '推荐计划条款 | LunarCrush',
      description: 'LunarCrush 推荐计划的条款与条件,包括奖励、支付和参与者责任。',
      ogTitle: 'LunarCrush 推荐计划条款',
      ogDescription: '管辖 LunarCrush 推荐计划的条款:奖励、支付和参与者责任。',
      ogType: 'website',
    },
  },
};

function pickLocale(raw: string | undefined): LocaleKey {
  if (!raw) return 'en';
  const k = raw.toLowerCase().split('-')[0] as LocaleKey;
  return TEMPLATES[k] ? k : 'en';
}

export function usePageMeta() {
  const { pathname } = useLocation();
  const { pages, useCases, blogPosts, faqs } = useCMS();
  const { i18n } = useTranslation();
  const locale = pickLocale(i18n.resolvedLanguage);
  const T = TEMPLATES[locale];

  useEffect(() => {
    let title = DEFAULT_TITLE;
    let desc = DEFAULT_DESC;
    let ogTitle = DEFAULT_TITLE;
    let ogDescription = DEFAULT_DESC;
    let ogType = 'website';
    // Research studies ship their own baked OG/social image (DEV-11563) rather
    // than the dynamic /api/og card.
    let ogImageOverride: string | undefined;
    let twitterDescOverride: string | undefined;

    // ── Dynamic per-item routes (DEV-10568) ──────────────────────
    // /use-cases/<slug>: look up the matching use case and feed its
    // title into the template. Each individual page now ships its
    // own <title> / description / OG instead of inheriting the
    // generic /use-cases index meta.
    const useCaseSlugMatch = /^\/use-cases\/([^/]+)$/.exec(pathname);
    const blogSlugMatch = /^\/blog\/([^/]+)$/.exec(pathname);
    const faqSlugMatch = /^\/support\/([^/]+)$/.exec(pathname);
    const metricSlugMatch = /^\/metrics\/([^/]+)$/.exec(pathname);
    // DEV-11563: tolerate the trailing slash. Prerendering the research routes
    // emits `dist/research/<slug>/index.html`, and Cloudflare Pages 308s the
    // bare path to the directory form — so the live pathname is
    // `/research/<slug>/`, which the original `$`-anchored pattern missed. That
    // silently dropped the page back to the default site title at runtime.
    // Every other landing route (`/about/`, `/pricing/`) already resolves with
    // the slash for the same reason.
    const researchSlugMatch = /^\/research\/([^/]+)\/?$/.exec(pathname);
    const isResearchIndex = pathname === '/research' || pathname === '/research/';

    let resolved = false;

    // ── Metrics glossary (DEV-10856) ─────────────────────────────
    // Index + per-metric detail pages ship English meta straight from
    // the metrics data module (these are English SEO pages).
    if (pathname === '/metrics') {
      title = METRICS_INDEX_TITLE;
      desc = METRICS_INDEX_DESC;
      ogTitle = 'LunarCrush Metrics Glossary';
      ogDescription = METRICS_INDEX_DESC;
      ogType = 'website';
      resolved = true;
    } else if (metricSlugMatch) {
      const slug = decodeURIComponent(metricSlugMatch[1]);
      const metric = getMetricBySlug(slug);
      if (metric && metric.published) {
        title = metric.metaTitle;
        desc = metric.metaDescription;
        ogTitle = metric.name;
        ogDescription = metric.summary;
        ogType = 'article';
        resolved = true;
      }
    } else if (isResearchIndex) {
      // ── Research hub (DEV-11563) ───────────────────────────────
      // English SEO surface for the institutional funnel.
      title = RESEARCH_INDEX_META.title;
      desc = RESEARCH_INDEX_META.description;
      ogTitle = 'LunarCrush Research';
      ogDescription = RESEARCH_INDEX_META.description;
      ogType = 'website';
      resolved = true;
    } else if (researchSlugMatch) {
      const slug = decodeURIComponent(researchSlugMatch[1]);
      const study = getStudyBySlug(slug);
      if (study) {
        title = study.metaTitle;
        desc = study.metaDescription;
        ogTitle = study.ogTitle;
        ogDescription = study.ogDescription;
        ogType = 'article';
        ogImageOverride = study.ogImage;
        twitterDescOverride = study.twitterDescription;
        resolved = true;
      }
    }

    if (resolved) {
      // already populated above — skip the dynamic CMS lookups below.
    } else if (useCaseSlugMatch) {
      const slug = decodeURIComponent(useCaseSlugMatch[1]);
      const uc = useCases.find((u) => useCaseSlug(u.title) === slug);
      if (uc) {
        const name = uc.title;
        title = T.useCase.title(name);
        desc = T.useCase.description(name);
        ogTitle = T.useCase.ogTitle(name);
        ogDescription = T.useCase.ogDescription(name);
        ogType = T.useCase.ogType;
        resolved = true;
      }
    } else if (blogSlugMatch) {
      const slug = decodeURIComponent(blogSlugMatch[1]);
      const post = blogPosts.find((p) => p.slug === slug);
      if (post) {
        const name = post.title;
        title = T.blog.title(name);
        ogTitle = T.blog.ogTitle(name);
        ogType = T.blog.ogType;
        // Description / og:description come from the CMS body —
        // the doc explicitly says "pull first ~155 chars of body
        // OR meta-description field if present".
        desc = post.seoDescription || post.excerpt || plainExcerpt(post.content, 155);
        ogDescription = post.seoDescription || post.excerpt || plainExcerpt(post.content, 110);
        resolved = true;
      }
    } else if (faqSlugMatch) {
      const slug = decodeURIComponent(faqSlugMatch[1]);
      const faq = faqs.find((f) => f.slug === slug);
      if (faq) {
        const name = faq.question;
        title = T.faq.title(name);
        ogTitle = T.faq.ogTitle(name);
        ogType = T.faq.ogType;
        const body = faq.answer || faq.content;
        desc = plainExcerpt(body, 155);
        ogDescription = plainExcerpt(body, 110);
        resolved = true;
      }
    }

    if (!resolved) {
      const pageKey = ROUTE_TO_PAGE[pathname];
      const page = pageKey ? pages[pageKey] : undefined;
      const override = ROUTE_OVERRIDES[pathname]?.[locale] ?? ROUTE_OVERRIDES[pathname]?.en;

      title = override?.title || page?.page_title || DEFAULT_TITLE;
      desc = override?.description || page?.meta_description || DEFAULT_DESC;
      // og:title / og:description ship separately from page_title /
      // meta_description; fall back to the page versions when the
      // CMS doesn't carry dedicated og fields yet.
      ogTitle = override?.ogTitle || page?.og_title || title;
      ogDescription = override?.ogDescription || page?.og_description || desc;
      ogType = override?.ogType || page?.og_type || 'website';
    }

    // DEV-10740: og:image now points at the dynamic generator
    // (functions/api/og) so the in-app share card matches the
    // crawler-facing one baked by scripts/generate-og-pages.ts. `/` maps
    // to `/api/og/home` (the empty `/api/og/` path serves the dev gallery).
    // Dynamic detail pages ride their resolved header along as `?t=` so the
    // card headline matches the page — the edge can't read the CMS.
    const ogPath = pathname === '/' ? '/home' : pathname;
    let ogImage = ogImageOverride ?? `${BASE_URL}/api/og${ogPath}`;
    if (!ogImageOverride && resolved && (blogSlugMatch || useCaseSlugMatch || faqSlugMatch)) {
      ogImage += `?t=${encodeURIComponent(ogTitle)}`;
    }
    const ogUrl = `${BASE_URL}${pathname === '/' ? '' : pathname}`;

    document.title = title;

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', desc);
    }

    // Open Graph
    setMetaTag('og:title', ogTitle);
    setMetaTag('og:description', ogDescription);
    setMetaTag('og:image', ogImage);
    setMetaTag('og:url', ogUrl);
    setOgType(ogType);

    // Twitter
    setMetaTag('twitter:title', ogTitle, true);
    setMetaTag('twitter:description', twitterDescOverride ?? ogDescription, true);
    setMetaTag('twitter:image', ogImage, true);
  }, [pathname, pages, useCases, blogPosts, faqs, T]);
}
